/*
 * File: TextInput.tsx
 * Author: Austin Robinson
 * Date Created: 10/18/2023
 * Copyright (c) DrivingDoc 2023
 */
import { useState } from 'react';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';

interface PropTypes extends Omit<OutlinedTextFieldProps, 'variant'> {
  name: string;
  label: string;
}

export default function TextInput({ name, label, helperText, ...textFieldProps }: PropTypes) {
  const { control } = useFormContext();
  const [showPassword, setShowPassword] = useState(textFieldProps?.type !== 'password');
  return (
    <Box>
      {label && (
        <Typography
          component={InputLabel}
          htmlFor={name}
          variant="subtitle1"
          color="text.primary"
          fontWeight={700}
          disabled={textFieldProps.disabled}
        >
          {label}
        </Typography>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <TextField
            id={name}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error?.message : helperText}
            inputRef={field.ref}
            value={field.value ?? ''}
            onBlur={field.onBlur}
            onChange={(event) => {
              field.onChange(event.target.value);
            }}
            {...textFieldProps}
            type={showPassword ? 'text' : (textFieldProps?.type ?? 'text')}
            InputProps={{
              name: field.name,
              endAdornment:
                textFieldProps?.type === 'password' ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      // onMouseDown={() => setShowPassword(true)}
                      // onMouseUp={() => setShowPassword(false)}
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            }}
          />
        )}
      />
    </Box>
  );
}
